import { IntlShape } from 'react-intl';

import { TranslatableField } from '@provider-portal/common/api/types';
import config from '@provider-portal/common/config';
import { LocaleToLanguageMap, LocaleType } from '@provider-portal/common/utils/translations_types';

import { Nullable } from '../types/types';
import { getStorageItem, setStorageItem } from './storage';
import { devWarn } from './utils';

export { LocaleType, LocaleToLanguageMap };

export type AppIntl = Omit<IntlShape, 'locale'> & { locale: LocaleType };

export const SupportedLocales: LocaleType[] = config.supportedLocales;
export const SupportedLanguages: Array<keyof typeof LocaleType> = config.supportedLocales.map(
  (localeKey) => LocaleToLanguageMap[localeKey],
);

const LOCAL_STORAGE_LANG_KEY = `${config.appName}.languageLocale`;

export class LanguageUtils {
  private static findLocaleByShortLanguageCode(language: string): Nullable<LocaleType> {
    return SupportedLocales.find((locale) => locale.startsWith(language)) || null;
  }

  public static getIsValidLocale(locale: any): locale is LocaleType {
    return !!SupportedLocales.find((key) => key === (locale as LocaleType));
  }

  // Gets default configured language
  public static getDefaultLocale(): LocaleType {
    return config.defaultLocale || LocaleType.English;
  }

  // Gets a heuristic guess based on browser locale / language, or the default locale
  public static getFallbackLocale(): LocaleType {
    const browserLocale = window.navigator.language as LocaleType;
    const browserLanguage = browserLocale.slice(0, 2); // "en-US" => "en"

    return (
      (LanguageUtils.getIsValidLocale(browserLocale) && browserLocale) || // Use browser locale, if supported
      LanguageUtils.findLocaleByShortLanguageCode(browserLanguage) || // Find best matching locale, using browser's language
      LanguageUtils.getDefaultLocale()
    );
  }

  // Gets and sets the configured language from the local storage
  public static getAppLocaleFromLocalStorage(): Nullable<LocaleType> {
    const appLocale = getStorageItem<LocaleType>(LOCAL_STORAGE_LANG_KEY);
    if (appLocale && LanguageUtils.getIsValidLocale(appLocale)) {
      return appLocale;
    }

    return null;
  }

  public static setAppLocaleToLocalStorage(appLocale: LocaleType): boolean {
    if (!appLocale || !LanguageUtils.getIsValidLocale(appLocale)) {
      return false;
    }

    setStorageItem(LOCAL_STORAGE_LANG_KEY, appLocale);
    return true;
  }

  // The complete logic for getting the current App locale (local storage, fallback, default)
  public static getAppLocaleWithFallback(): LocaleType {
    return (
      LanguageUtils.getAppLocaleFromLocalStorage() ||
      LanguageUtils.getFallbackLocale() ||
      LanguageUtils.getDefaultLocale()
    );
  }

  public static setLocale(locale): boolean {
    if (!LanguageUtils.getIsValidLocale(locale)) {
      return false;
    }

    LanguageUtils.setAppLocaleToLocalStorage(locale);
    return true;
  }
}

// Clean translatable field of empty languages
export function cleanTranslatableFieldOfEmptyLanguages(
  translatableField: TranslatableField,
): TranslatableField {
  const translationFieldToProcess = { ...translatableField };
  for (const language of Object.keys(translationFieldToProcess)) {
    if (!translationFieldToProcess[language]) {
      delete translationFieldToProcess[language];
    }
  }
  return translationFieldToProcess;
}

export function translateField(field: TranslatableField, locale = LocaleType.English): string {
  if (field[locale] !== undefined) {
    return field[locale];
  }
  const anyField = Object.entries(field)[0];

  if (anyField) {
    devWarn(`No "${locale}" locale found, falling back "${anyField[0]}" - "${anyField[1]}`);
    return anyField[1];
  }

  const fieldJSON = JSON.stringify(field);
  devWarn(`Translation is empty, falling back to JSON "${fieldJSON}"`);
  return fieldJSON;
}

export function setAppLocale(locale: LocaleType) {
  const isSetLocaleSuccessful = LanguageUtils.setLocale(locale);
  if (isSetLocaleSuccessful) {
    try {
      window.location.reload();
      return true;
    } catch (_e) {
      return false;
    }
  }
  return isSetLocaleSuccessful;
}

export function normaliseLocaleString(locale: string | undefined) {
  if (!locale) {
    return config.defaultLocale;
  }
  if (locale.length === 2) {
    const matchedLocale = SupportedLocales.find((key) => {
      const keyParts = key.split('-');
      return keyParts[0] === locale;
    });
    if (!matchedLocale) {
      return config.defaultLocale;
    }
    return matchedLocale;
  }

  if (locale.length === 5) {
    const localeParts = locale.split('-');
    if (localeParts.length !== 2) {
      return config.defaultLocale;
    }
    return `${localeParts[0]}-${localeParts[1].toUpperCase()}`;
  }
  return config.defaultLocale;
}
